import { FieldsKey, MediaFields } from "./types"
import {
  getAppliedPercentageOrFixed,
  getMediaFieldBySemantic,
  getMediaFieldValue,
  roundHalfEven,
  setMediaFieldValue,
} from "./utils"

import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { ChangedPlanRecord } from "../../types"
import { Commission } from "models/commissions"
import { FeeOption } from "api/plans"
import { MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { TaxUsage } from "models/taxes"
import { calculationContext } from "./calculations"

export function calculateFinancials(li: ChangedPlanRecord, precision: number) {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return li

  // roi
  const withROI = calculateROI(li)

  // guards for the rest of the financials
  const grossCostField = getMediaFieldBySemantic("grossCost")
  const billingTypeField = getMediaFieldBySemantic("billingType")

  if (!grossCostField || !billingTypeField) return withROI

  const billingType = getMediaFieldValue<BillingType["semanticCode"]>(
    fields,
    billingTypeField,
  )

  const withMCAndNetCost = calculateMCAndNetCost(withROI, precision)

  const withBillable = calculateBillable(withMCAndNetCost, billingType)

  const withBudget = assignBudget(withBillable ?? withMCAndNetCost)
  return withBudget
}

function assignBudget(li: ChangedPlanRecord): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const budgetField = getMediaFieldBySemantic("budget")

  switch (cc.budgetPlanning) {
    case "GROSS_COST": {
      const grossCostField = getMediaFieldBySemantic("grossCost")
      if (!grossCostField || !budgetField) return li

      const grossCostValue = getMediaFieldValue<number>(fields, grossCostField)
      const withBudget = setMediaFieldValue(grossCostValue, fields, budgetField)

      return { ...li, [cc.fieldsKey]: withBudget }
    }
    case "NET_COST": {
      const netCostField = getMediaFieldBySemantic("netCost")
      if (!netCostField || !budgetField) return li

      const netCostValue = getMediaFieldValue<number>(fields, netCostField)
      const withBudget = setMediaFieldValue(netCostValue, fields, budgetField)

      return { ...li, [cc.fieldsKey]: withBudget }
    }
    default: {
      const billableField = getMediaFieldBySemantic("billable")
      if (!billableField || !budgetField) return li

      const billableValue = getMediaFieldValue<number>(fields, billableField)
      const withBudget = setMediaFieldValue(billableValue, fields, budgetField)

      return { ...li, [cc.fieldsKey]: withBudget }
    }
  }
}

function calculateBillable(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
): ChangedPlanRecord {
  // fees
  const {
    totalFees,
    totalVF,
    totalAF,
    li: withUpdatedFees,
  } = calculateFeesAndAssignValues(li, billingType)

  const cc = calculationContext
  const isBooking = cc.fieldsKey === "buyingMediaFields"

  // agency commissions
  const { totalAC, li: withUpdatedAC } = isBooking
    ? { totalAC: 0, li: withUpdatedFees }
    : calculateACsAndAssignValues(withUpdatedFees, billingType, totalVF)

  // taxes
  const { li: withACTaxes, total: acTaxes } = calculateACTaxesAndAssignValues(
    withUpdatedAC,
    billingType,
    totalAC + totalAF,
    isBooking,
  )
  const { li: withMCTaxes, total: mcTaxes } = calculateMCTaxesAndAssignValues(
    withACTaxes,
    billingType,
    totalVF,
  )

  const withBillable = sumAndAssignBillable(withMCTaxes, billingType, {
    totalAC,
    totalFees,
    acTaxes,
    mcTaxes,
  })

  return withBillable
}

function sumAndAssignBillable(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
  totals: {
    totalAC?: number
    totalFees?: number
    acTaxes?: number
    mcTaxes?: number
  } = {},
) {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const effectiveNetCost = getEffectiveNetCost(fields, billingType)
  const { totalFees = 0, totalAC = 0, acTaxes = 0, mcTaxes = 0 } = totals
  const totalTaxes = roundHalfEven(acTaxes + mcTaxes)

  const billableField = getMediaFieldBySemantic("billable")
  const taxesField = getMediaFieldBySemantic("taxes")

  const billableSum =
    !billingType || billingType === "bonus"
      ? effectiveNetCost + totalTaxes
      : effectiveNetCost + totalAC + totalFees + totalTaxes

  const billableValue = roundHalfEven(billableSum)

  if (!billableField || !taxesField) return li

  const withTaxes = setMediaFieldValue(totalTaxes, fields, taxesField, [
    "total",
  ])

  if (billableField.type === "Calculated") {
    return { ...li, [cc.fieldsKey]: withTaxes }
  }

  const withBillable = setMediaFieldValue(
    billableValue,
    withTaxes,
    billableField,
  )

  return { ...li, [cc.fieldsKey]: withBillable }
}

function calculateMCTaxesAndAssignValues(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
  mcFees?: number,
) {
  let total = 0

  if (!billingType || billingType === "bonus") return { li, total }

  return getMCTax(li, mcFees ?? 0, billingType)
}

function calculateACTaxesAndAssignValues(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
  totalAC?: number,
  isBooking?: boolean,
) {
  let total = 0

  if (!billingType || billingType === "bonus" || isBooking) return { li, total }

  return getACTax(li, totalAC ?? 0)
}

function calculateACsAndAssignValues(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
  totalVF?: number,
): { totalAC: number; li: ChangedPlanRecord } {
  const cc = calculationContext
  const ac = getAgencyCommission(li)
  let totalAC = 0

  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]
  const agencyCommissionsField = getMediaFieldBySemantic("agencyCommissions")

  if (!agencyCommissionsField) return { totalAC, li }

  let withAssignedAC = { ...fields }

  const billingTypeApplicable =
    !!billingType && ["paid", "ContraAc"].includes(billingType)

  if (!ac || !billingTypeApplicable) {
    withAssignedAC =
      setMediaFieldValue(
        { commission: ac, value: 0 },
        fields,
        agencyCommissionsField,
      ) || {}

    const updatedLi = { ...li, [cc.fieldsKey]: withAssignedAC }

    return { totalAC, li: updatedLi }
  }

  switch (ac?.computedOn) {
    case "GROSS":
      {
        const grossCostField = getMediaFieldBySemantic("grossCost")
        if (!!grossCostField) {
          const grossCost =
            getMediaFieldValue<number>(fields, grossCostField) ?? 0

          const appliedAmount = !grossCost
            ? 0
            : getAppliedPercentageOrFixed(
                grossCost,
                ac?.amount || 0,
                "PERCENTAGE",
              )

          totalAC = appliedAmount || 0

          const acCommission = {
            commission: ac,
            value: roundHalfEven(appliedAmount),
          }

          withAssignedAC =
            setMediaFieldValue(
              acCommission,
              withAssignedAC,
              agencyCommissionsField,
            ) || {}
        }
      }
      break
    case "NET":
      {
        // we need to consider the actual net cost
        const netCost = getActualNetCost(fields)

        if (netCost) {
          const appliedAmount = getAppliedPercentageOrFixed(
            netCost,
            ac?.amount || 0,
            "PERCENTAGE",
          )

          // in case of ContraAc or ContraNoAc do not add to totalAC
          totalAC = appliedAmount || 0

          const acCommission = {
            commission: ac,
            value: roundHalfEven(appliedAmount),
          }

          withAssignedAC =
            setMediaFieldValue(
              acCommission,
              withAssignedAC,
              agencyCommissionsField,
            ) || {}
        }
      }
      break
    case "PRODUCTION":
      if (totalVF) {
        const appliedAmount = getAppliedPercentageOrFixed(
          totalVF,
          ac?.amount || 0,
          "PERCENTAGE",
        )

        totalAC = appliedAmount || 0

        const acCommission = {
          commission: ac,
          value: roundHalfEven(appliedAmount),
        }

        withAssignedAC =
          setMediaFieldValue(
            acCommission,
            withAssignedAC,
            agencyCommissionsField,
          ) || {}
        // if not vendor(media) fees nullify ac commissions
      } else {
        withAssignedAC =
          setMediaFieldValue(
            undefined,
            withAssignedAC,
            agencyCommissionsField,
          ) || {}
      }
      break
    default:
      break
  }

  const updatedLi = { ...li, [cc.fieldsKey]: withAssignedAC }
  return { totalAC, li: updatedLi }
}

export function calculateFeesAndAssignValues(
  li: ChangedPlanRecord,
  billingType?: BillingType["semanticCode"],
  externalContext?: {
    fieldsKey: FieldsKey
    media: MediaType
    fees?: FeeOption[]
    passthroughOnly?: boolean
  },
): {
  totalFees: number
  totalVF: number
  totalAF: number
  individualFees: Array<{ fee: FeeOption; value: number }>
  li: ChangedPlanRecord
} {
  const cc = externalContext || calculationContext
  const isBooking = cc.fieldsKey === "buyingMediaFields"
  const fields = li[cc.fieldsKey] as
    | ChangedPlanRecord[typeof cc.fieldsKey]
    | undefined

  let totalFees = 0
  let totalAF = 0
  let totalVF = 0
  let individualFees: Array<{ fee: FeeOption; value: number }> = []

  if (!fields) return { totalFees, totalVF, totalAF, individualFees, li }

  const feeFields = cc.media?.mediaFields?.filter((mf) => mf.type === "Fees")
  const appliedFeeFields = feeFields?.filter((af) => !!fields[af.id])
  let withAssignedFees = { ...fields }

  const feesAmount =
    appliedFeeFields?.reduce((sum, feeField) => {
      let feeAmount = 0
      const isAgencyFee = feeField.semanticCode !== "passthroughCosts"

      const fee = getMediaFieldValue<{ fees?: FeeOption[] }>(
        withAssignedFees,
        feeField,
      )

      let fees = fee?.fees

      /*
        apply default if fees are empty. In case of BO do not apply
        defaults for agency fees.
      */
      if (!fee && !!cc.fees?.length) {
        fees = [cc.fees[0]]
        if (isBooking && isAgencyFee) fees = []
      }

      // filter selected fees based on the available ones
      const availableFeeIds = cc.fees?.map((f) => f.id)
      fees = fees?.filter((f) =>
        !!cc.fees?.length ? availableFeeIds?.includes(f.id) : true,
      )

      // reset value
      withAssignedFees =
        setMediaFieldValue({ value: 0, fees }, withAssignedFees, feeField) || {}
      fees?.forEach((singleFee) => {
        const { computedOn, amountType, amount } = singleFee

        switch (computedOn) {
          case "GROSS":
            {
              const grossCostField = getMediaFieldBySemantic(
                "grossCost",
                externalContext,
              )
              if (!!grossCostField) {
                const grossCost =
                  getMediaFieldValue<number>(fields, grossCostField) ?? 0

                const appliedAmount =
                  (!grossCost && amountType !== "FIXED_AMOUNT") ||
                  (isBooking && isAgencyFee)
                    ? 0
                    : getAppliedPercentageOrFixed(grossCost, amount, amountType)

                feeAmount += appliedAmount
                const feeFieldValue = { fees, value: roundHalfEven(feeAmount) }

                withAssignedFees =
                  setMediaFieldValue(
                    feeFieldValue,
                    withAssignedFees,
                    feeField,
                  ) || {}

                if (!isAgencyFee) {
                  !!externalContext?.passthroughOnly &&
                    individualFees.push({
                      fee: singleFee,
                      value: appliedAmount,
                    })
                  totalVF += appliedAmount
                } else {
                  !externalContext?.passthroughOnly &&
                    individualFees.push({
                      fee: singleFee,
                      value: appliedAmount,
                    })
                  totalAF += appliedAmount
                }

                sum += appliedAmount
              }
            }
            break
          case "NET":
          case "PRODUCTION":
            {
              const isComputedOnNet = computedOn === "NET"
              // we need to consider the actual net cost in case of NET
              const netCost = isComputedOnNet
                ? getActualNetCost(fields, externalContext)
                : getEffectiveNetCost(fields, billingType, externalContext)

              if (netCost || amountType === "FIXED_AMOUNT") {
                const appliedAmount =
                  isBooking && isAgencyFee
                    ? 0
                    : getAppliedPercentageOrFixed(netCost, amount, amountType)

                feeAmount += appliedAmount
                const feeFieldValue = { fees, value: roundHalfEven(feeAmount) }

                withAssignedFees =
                  setMediaFieldValue(
                    feeFieldValue,
                    withAssignedFees,
                    feeField,
                  ) || {}

                if (!isAgencyFee) {
                  !!externalContext?.passthroughOnly &&
                    individualFees.push({
                      fee: singleFee,
                      value: appliedAmount,
                    })
                  totalVF += appliedAmount
                } else {
                  !externalContext?.passthroughOnly &&
                    individualFees.push({
                      fee: singleFee,
                      value: appliedAmount,
                    })
                  totalAF += appliedAmount
                }

                sum += appliedAmount
              }
            }
            break
        }
      })

      return sum
    }, 0) ?? 0

  totalFees = isNaN(feesAmount) ? 0 : feesAmount
  const updatedLi = { ...li, [cc.fieldsKey]: withAssignedFees }
  return { totalFees, totalAF, totalVF, individualFees, li: updatedLi }
}

function calculateMCAndNetCost(
  li: ChangedPlanRecord,
  precision: number,
): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return li

  const grossCostField = getMediaFieldBySemantic("grossCost")
  const mcField = getMediaFieldBySemantic("mediaCommissions")
  const netCostField = getMediaFieldBySemantic("netCost")
  const billingTypeField = getMediaFieldBySemantic("billingType")

  if (!grossCostField || !netCostField || !mcField) return li

  const grossCost = getMediaFieldValue<number>(fields, grossCostField) ?? 0

  const billingType = billingTypeField
    ? getMediaFieldValue<BillingType["semanticCode"]>(fields, billingTypeField)
    : undefined

  if (
    (!billingType || billingType === "bonus") &&
    netCostField.type !== "Calculated"
  ) {
    const withNetCost = setMediaFieldValue(0, fields, netCostField)

    return { ...li, [cc.fieldsKey]: withNetCost }
  }

  const mediaCommission = getMediaCommission(li)

  const mediaCommissionValue =
    mediaCommission && grossCost
      ? getAppliedPercentageOrFixed(
          grossCost,
          Number(mediaCommission.amount),
          "PERCENTAGE",
          precision,
        )
      : undefined

  const mcFieldValue = mediaCommission
    ? {
        commission: mediaCommission,
        value: mediaCommissionValue
          ? roundHalfEven(mediaCommissionValue, precision)
          : undefined,
      }
    : mediaCommission

  const withMC = setMediaFieldValue(mcFieldValue, fields, mcField)

  if (netCostField.type === "Calculated") {
    return { ...li, [cc.fieldsKey]: withMC }
  }

  const netCostValue = grossCost
    ? roundHalfEven(Number(grossCost) - (mediaCommissionValue ?? 0), precision)
    : 0

  const withMCAndNetCost = setMediaFieldValue(
    netCostValue,
    withMC,
    netCostField,
  )

  return { ...li, [cc.fieldsKey]: withMCAndNetCost }
}

function getMCTax(
  li: ChangedPlanRecord,
  mcFees: number,
  billingType: BillingType["semanticCode"],
) {
  let mcTaxAmount = 0
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]
  const mcTaxes = cc.taxes?.MC?.filter((t) => t.isEnabled)
  const taxField = getMediaFieldBySemantic("taxes")

  if (!mcTaxes?.length || !taxField) return { li, total: mcTaxAmount }

  const { mediaTax } = getMediaFieldValue<TaxUsage>(fields, taxField) || {}
  let mcTax = mediaTax?.tax

  // if empty apply default or first one
  if (!mcTax) {
    mcTax = mcTaxes?.find((t) => t.isDefault) || mcTaxes?.[0]
  }

  if (!mcTax) return { li, total: mcTaxAmount }

  const effectiveNetCost = getEffectiveNetCost(li[cc.fieldsKey], billingType)
  const appliedOn = effectiveNetCost + mcFees

  const { amount, amountType } = mcTax
  mcTaxAmount =
    amountType === "PERCENTAGE"
      ? appliedOn * ((amount ?? 0) * 0.01)
      : (amount ?? 0)
  const roundedMCTax = roundHalfEven(mcTaxAmount)

  const withMCTaxes = setMediaFieldValue(
    { value: roundedMCTax, tax: mcTax },
    fields,
    taxField,
    ["mediaTax"],
  )

  return { li: { ...li, [cc.fieldsKey]: withMCTaxes }, total: roundedMCTax }
}

function getACTax(li: ChangedPlanRecord, totalAC: number) {
  let acTaxAmount = 0
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]
  const acTaxes = cc.taxes?.AC?.filter((t) => t.isEnabled)
  const taxField = getMediaFieldBySemantic("taxes")

  if (!acTaxes?.length || !taxField) return { li, total: acTaxAmount }

  const { agencyTax } = getMediaFieldValue<TaxUsage>(fields, taxField) || {}
  let acTax = agencyTax?.tax

  // if empty apply default or first one
  if (!acTax) {
    acTax = acTaxes?.find((t) => t.isDefault) || acTaxes?.[0]
  }

  if (!acTax) return { li, total: acTaxAmount }

  const { amount, amountType } = acTax
  acTaxAmount =
    amountType === "PERCENTAGE"
      ? totalAC * ((amount ?? 0) * 0.01)
      : (amount ?? 0)
  const roundedACTax = roundHalfEven(acTaxAmount)

  const withAcTax = setMediaFieldValue(
    { value: roundedACTax, tax: acTax },
    fields,
    taxField,
    ["agencyTax"],
  )

  return { li: { ...li, [cc.fieldsKey]: withAcTax }, total: roundedACTax }
}

function getAgencyCommission(
  li: ChangedPlanRecord,
): Commission | null | undefined {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return

  const acField = getMediaFieldBySemantic("agencyCommissions")
  if (!acField) return

  let agencyCommission

  if (acField) {
    const agencyCommissionField = getMediaFieldValue<{
      value?: number
      commission?: Commission
    }>(fields, acField)

    // remove the commission if not currently available
    const availableACCommissionIds = cc.acCommissions?.map((acc) => acc.id)
    const agencyCommissionId = agencyCommissionField?.commission?.id

    if (
      !!agencyCommissionId &&
      !availableACCommissionIds?.includes(agencyCommissionId)
    ) {
      return agencyCommission
    }

    let defaultAC = null
    // Check if user selection is not null
    if (agencyCommissionField?.commission !== null) {
      // Check if agencyCommissionField and agencyCommission are undefined
      if (agencyCommissionField === undefined || !agencyCommission)
        defaultAC = cc.acCommissions?.[0]
    }

    agencyCommission =
      cc.acCommissions?.find((acc) => acc.id === agencyCommissionId) ||
      defaultAC
  }

  return agencyCommission
}

function getMediaCommission(
  li: ChangedPlanRecord,
): Commission | null | undefined {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return

  const mcField = getMediaFieldBySemantic("mediaCommissions")
  if (!mcField) return

  let mediaCommission

  if (mcField) {
    const mediaCommissionField = getMediaFieldValue<{
      value?: number
      commission?: Commission
    }>(fields, mcField)

    const availableMCCommissionIds = cc.mcCommissions?.map((mcc) => mcc.id)
    const mediaCommissionId = mediaCommissionField?.commission?.id

    // remove the commission if not in the available ones
    if (
      !!mediaCommissionId &&
      !availableMCCommissionIds?.includes(mediaCommissionId)
    ) {
      return mediaCommission
    }

    let defaultMC = null
    // Check if user selection is not null
    if (mediaCommissionField?.commission !== null) {
      // Check if mediaCommissionField and mediaCommission are undefined
      if (mediaCommissionField === undefined || !mediaCommission)
        defaultMC = cc.mcCommissions?.[0]
    }

    mediaCommission =
      cc.mcCommissions?.find((mcc) => mcc.id === mediaCommissionId) || defaultMC
  }

  return mediaCommission
}

function calculateROI(li: ChangedPlanRecord): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return li

  const grossCostField = getMediaFieldBySemantic("grossCost")
  const mediaValueField = cc.media?.mediaFields?.find(
    (mf) => mf.semanticCode === "mediaValue",
  )
  const roiField = cc.media?.mediaFields?.find(
    (mf) => mf.semanticCode === "roi",
  )

  if (!grossCostField || !mediaValueField || !roiField) return li

  const grossCost = getMediaFieldValue<number>(fields, grossCostField) ?? 0
  const mediaValue = getMediaFieldValue<number>(fields, mediaValueField) ?? 0

  const roiValue =
    grossCost && mediaValue ? Number(mediaValue) / Number(grossCost) : 0

  const roundedRoi = roundHalfEven(roiValue)

  const withROI = setMediaFieldValue(roundedRoi, fields, roiField)

  return { ...li, [cc.fieldsKey]: withROI }
}

function getEffectiveNetCost(
  fields: MediaFields,
  billingType?: BillingType["semanticCode"],
  externalContext?: { media: MediaType },
) {
  const cc = externalContext || calculationContext
  const media = cc.media

  if (!media) return 0
  if (!billingType || ["ContraAc", "ContraNoAc"].includes(billingType)) {
    return 0
  }

  const netCostField = getMediaFieldBySemantic("netCost", externalContext)
  if (!!netCostField) {
    const netCost = getMediaFieldValue<number>(fields, netCostField)

    return !!netCost ? roundHalfEven(netCost) : 0
  }

  return 0
}

function getActualNetCost(
  fields: MediaFields,
  externalContext?: { media: MediaType },
) {
  return getEffectiveNetCost(fields, "Paid", externalContext)
}
